import { Rol } from "@/types/roles";

type NavItem = {
  name: string;
  icon?: React.ReactNode;
  path?: string;
  subItems?: NavItem[];
  roles?: Rol[];
  onClick?: () => void;
};

import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "@/context/AuthContext";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import {
  ChevronDown,
  List,
  Plus,
  MapPin,
  Settings,
  Code,
  Briefcase,
  User,
  FileQuestion,
  Download,
  Upload,
  FileQuestionIcon,
  LayoutGrid,
  MapIcon,
  UserPlus,
} from "lucide-react";

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const { user, isMobileApp, token, configuracion } = useAuth();
  const userRol: Rol | undefined = user?.rol;
  const { downloadInfo, uploadInfo, syncing } = useOfflineSync();
  const backend_api = import.meta.env.VITE_API_URL_BACKEND;
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const getLogoUrl = async (logoPath: string) => {
    if (!logoPath) return null;

    const filename = logoPath.split("/").pop();
    const response = await fetch(`${backend_api}/files/image/${filename}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) return null;

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };


  useEffect(() => {
    if (configuracion && configuracion.logo) {
      getLogoUrl(configuracion.logo).then((url) => setLogoUrl(url));
    } else {
      setLogoUrl(null);
    }
  }, [configuracion]);

  const AdminItems: NavItem[] = [
    {
      name: "Gestion Global",
      icon: <LayoutGrid size={25} />,
      roles: [Rol.ADMIN_EMPRESAS],
      subItems: [
        {
          name: "Sucursales",
          icon: <MapPin size={22} />,
          roles: [Rol.ADMIN_EMPRESAS],
          subItems: [
            {
              name: "Ver Sucursales",
              path: "/multiempresa/sucursales",
              icon: <List size={20} />,
              roles: [Rol.ADMIN_EMPRESAS],
            },
            {
              name: "Crear Sucursal",
              path: "/multiempresa/sucursales/create",
              icon: <Plus size={20} />,
              roles: [Rol.ADMIN_EMPRESAS],
            },
          ],
        },
        {
          name: "Configuracion",
          icon: <Settings size={22} />,
          roles: [Rol.ADMIN_EMPRESAS],
          subItems: [
            {
              name: "Ver Configuraciones",
              path: "/multiempresa/configuracion_empresas",
              icon: <List size={20} />,
              roles: [Rol.ADMIN_EMPRESAS],
            },
            {
              name: "Crear Configuracion",
              path: "/multiempresa/configuracion_empresas/create",
              icon: <Plus size={20} />,
              roles: [Rol.ADMIN_EMPRESAS],
            },
          ],
        },
      ],
    },

    {
      name: "Seguridad de accesos",
      icon: <Code size={25} />,
      roles: [Rol.ADMIN_EMPRESAS],
      subItems: [
        {
          name: "Usuarios",
          icon: <User size={22} />,
          roles: [Rol.ADMIN_EMPRESAS],
          subItems: [
            {
              name: "Ver Usuarios",
              path: "/seguridad_accesos/usuarios",
              icon: <List size={20} />,
              roles: [Rol.ADMIN_EMPRESAS],
            },
            {
              name: "Crear Usuario",
              path: "/seguridad_accesos/usuarios/create",
              icon: <Plus size={20} />,
              roles: [Rol.ADMIN_EMPRESAS],
            },
            {
              name: "Importar Usuarios",
              path: "/seguridad_accesos/usuarios/masive",
              icon: <UserPlus size={20} />,
              roles: [Rol.ADMIN_EMPRESAS],
            },
          ],
        },
        {
          name: "Ver Sesiones",
          path: "/seguridad_accesos/sesiones",
          icon: <List size={20} />,
          roles: [Rol.ADMIN_EMPRESAS],
        },
      ],
    },

    {
      name: "Proyectos",
      icon: <Briefcase size={22} />,
      roles: [Rol.ADMIN_EMPRESAS, Rol.SUPERVISOR, Rol.COLABORADOR],
      subItems: [
        {
          name: "Listar Proyectos",
          path: "/gestion_proyectos/proyectos",
          icon: <List size={20} />,
          roles: [Rol.ADMIN_EMPRESAS, Rol.SUPERVISOR, Rol.COLABORADOR],
        },
        {
          name: "Crear Proyecto",
          path: "/gestion_proyectos/proyectos/create",
          icon: <Plus size={20} />,
          roles: [Rol.ADMIN_EMPRESAS, Rol.SUPERVISOR, Rol.COLABORADOR],
        },
      ],
    },

    {
      name: "Encuestas",
      icon: <FileQuestion size={22} />,
      roles: [Rol.ADMIN_EMPRESAS, Rol.SUPERVISOR, Rol.COLABORADOR],
      subItems: [
        {
          name: "Listar Encuestas",
          path: "/gestion_proyectos/survey/list",
          icon: <List size={20} />,
          roles: [Rol.ADMIN_EMPRESAS, Rol.SUPERVISOR, Rol.COLABORADOR],
        },
        {
          name: "Crear Encuesta",
          path: "/gestion_proyectos/survey/create",
          icon: <Plus size={20} />,
          roles: [Rol.ADMIN_EMPRESAS, Rol.SUPERVISOR, Rol.COLABORADOR],
        },
        {
          name: "Mapa de calor",
          path: "/gestion_proyectos/survey/map",
          icon: <MapIcon size={20} />,
          roles: [Rol.ADMIN_EMPRESAS, Rol.SUPERVISOR, Rol.COLABORADOR],
        }
      ],
    },
  ];

  const MobileItems: NavItem[] = [
    { name: "Gestion de proyectos" },
    {
      name: "Tareas",
      path: "/gestion_proyectos/tareas",
      icon: <List size={20} />,
    },
    {
      name: "Encuestas",
      path: "/gestion_proyectos/encuestas",
      icon: <FileQuestionIcon size={20} />,
    },
    { name: "Sincronizacion" },
    {
      name: syncing ? "Sincronizando..." : "Descargar Info",
      icon: <Download size={20} />,
      onClick: downloadInfo,
    },
    {
      name: syncing ? "Sincronizando..." : "Subir Info",
      icon: <Upload size={20} />,
      onClick: uploadInfo,
    },
  ];

  const menuToRender = isMobileApp ? MobileItems : AdminItems;

  const hasPermission = (item: NavItem): boolean => {
    if (!item.roles || item.roles.length === 0) return true;
    if (userRol === undefined || userRol === null) return false;
    // Aseguramos que la comparacion funcione tanto para strings como para numeros
    return item.roles.some((role) => Number(role) === Number(userRol));
  };

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname],
  );

  const toggleSubmenu = (key: string) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderMenuItemsRecursive = (
    items: NavItem[],
    parentKey: string = "",
    level: number = 0,
  ) => (
    <ul className="flex flex-col gap-1">
      {items.filter(hasPermission).map((item, index) => {
        const key = parentKey ? `${parentKey}-${index}` : `${index}`;
        const hasSubItems = !!item.subItems && item.subItems.length > 0;
        const isOpen = openSubmenus[key];

        const paddingLeftValue = level * 16 + (isMobileApp ? 10 : 0);

        return (
          <li key={key}>
            {hasSubItems ? (
              <button
                onClick={() => toggleSubmenu(key)}
                className={`menu-item group cursor-pointer flex items-center w-full ${isOpen ? "menu-item-active" : "menu-item-inactive"
                  }`}
                style={{ paddingLeft: `${paddingLeftValue}px` }}
              >
                {item.icon && (
                  <span className="mr-2 flex items-center justify-center">
                    {item.icon}
                  </span>
                )}
                <span className="menu-item-text">{item.name}</span>

                <ChevronDown
                  className={`ml-auto w-5 h-5 transition-transform duration-200 ${isOpen ? "rotate-180 text-brand-500" : ""
                    }`}
                />
              </button>
            ) : item.path ? (
              <Link
                to={item.path}
                className={`menu-item group flex items-center w-full ${isActive(item.path) ? "menu-item-active" : "menu-item-inactive"
                  }`}
                style={{ paddingLeft: `${paddingLeftValue}px` }}
              >
                {item.icon && (
                  <span className="mr-2 flex items-center justify-center">
                    {item.icon}
                  </span>
                )}
                <span className="menu-item-text">{item.name}</span>
              </Link>
            ) : item.onClick ? (
              <button
                onClick={item.onClick}
                className={`menu-item group flex items-center w-full menu-item-inactive`}
                style={{ paddingLeft: `${paddingLeftValue}px` }}
              >
                {item.icon && (
                  <span className="mr-2 flex items-center justify-center">
                    {item.icon}
                  </span>
                )}
                <span className="menu-item-text">{item.name}</span>
              </button>
            ) : (
              <div
                className={`flex items-center w-full py-2 px-4 text-sm font-semibold text-gray-500 dark:text-gray-400 ${isMobileApp ? "justify-center" : ""
                  }`}
                style={{
                  paddingLeft: isMobileApp ? "0px" : `${paddingLeftValue}px`,
                }}
              >
                {item.name}
              </div>
            )}

            {hasSubItems && (
              <div
                className={`transition-all duration-300 overflow-hidden ${isOpen ? "max-h-[2000px]" : "max-h-0"
                  }`}
                style={{ paddingLeft: `${16}px` }}
              >
                {renderMenuItemsRecursive(
                  item.subItems!.filter(hasPermission),
                  key,
                  level + 1,
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <aside
      className={`fixed mt-[calc(4rem+env(safe-area-inset-top))] flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 pt-[env(safe-area-inset-top)] 
        ${isExpanded || isMobileOpen
          ? "w-[290px]"
          : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-8 flex ${!isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
          }`}
      >
        <Link to="/home">
          {isExpanded || isHovered || isMobileOpen ? (
            logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                width={150}
                className="max-h-10 object-contain"
              />
            ) : (
              <>
                <img
                  className="dark:hidden"
                  src="/logo.png"
                  alt="Logo"
                  width={150}
                  height={40}
                />
                <img
                  className="hidden dark:block"
                  src="/favicon.png"
                  alt="Logo"
                  width={150}
                  height={40}
                />
              </>
            )
          ) : (
            <img
              src={logoUrl || "/favicon.png"}
              alt="Logo"
              width={32}
              height={32}
              className="object-contain rounded-lg"
            />
          )}
        </Link>
      </div>

      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div>{renderMenuItemsRecursive(menuToRender, "main")}</div>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;
